/**
 * Login Page
 * Phone + OTP authentication for vendors
 * Updated: Jan 21, 2026 - FrogBasket branding
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { sendOTP, verifyOTP } from '@/lib/auth';
import { parsePhoneNumber, formatPhoneNumber } from '@/lib/utils';
import { logger } from '@/lib/logger';
import { isFeatureEnabled } from '@/lib/featureFlags';

export default function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const setVendor = useAuthStore((state) => state.setVendor);

  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOTP] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Check if vendor dashboard is enabled
  const dashboardEnabled = isFeatureEnabled('vendorDashboard');

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!dashboardEnabled) {
      setError('Vendor dashboard is currently disabled. Please contact support.');
      return;
    }

    if (phone.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }

    setIsLoading(true);

    try {
      const parsedPhone = parsePhoneNumber(phone);
      await sendOTP(parsedPhone);
      setStep('otp');
      logger.info('OTP sent', { phone: parsedPhone });
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to send OTP';
      setError(message);
      logger.error('Failed to send OTP', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setIsLoading(true);

    try {
      const parsedPhone = parsePhoneNumber(phone);
      const result = await verifyOTP(parsedPhone, otp);

      setAuth(result.user, result.vendor);
      if (result.vendor) {
        setVendor(result.vendor);
      }

      logger.info('Login successful', { userId: result.user.id });
      navigate('/orders', { replace: true });
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to verify OTP';
      setError(message);
      logger.error('Failed to verify OTP', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setStep('phone');
    setOTP('');
    setError('');
  };

  if (!dashboardEnabled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-500 via-green-600 to-teal-700 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Dashboard Disabled</h2>
            <p className="text-gray-600">
              The vendor dashboard is currently disabled. Please contact support for assistance.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-500 via-green-600 to-teal-700 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            {/* FrogBasket Logo */}
            <div className="flex justify-center mb-4">
              <img 
                src="/frog_logo.png" 
                alt="FrogBasket" 
                className="w-20 h-20 object-contain"
                onError={(e) => {
                  // Fallback if logo not found
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
            <h1 className="text-3xl font-bold mb-2">
              <span className="text-emerald-600">Frog</span>
              <span className="text-amber-500">Basket</span>
            </h1>
            <p className="text-gray-600">Vendor Dashboard</p>
          </div>

          {/* Phone Step */}
          {step === 'phone' && (
            <form onSubmit={handlePhoneSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+63 9XX XXX XXXX"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  disabled={isLoading}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Sending OTP...' : 'Send OTP'}
              </button>
            </form>
          )}

          {/* OTP Step */}
          {step === 'otp' && (
            <form onSubmit={handleOTPSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="otp" className="block text-sm font-medium text-gray-700">
                    Verification Code
                  </label>
                  <button
                    type="button"
                    onClick={handleBack}
                    className="text-sm text-emerald-600 hover:text-emerald-700"
                  >
                    Change number
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Enter the 6-digit code sent to {formatPhoneNumber(parsePhoneNumber(phone))}
                </p>
                <input
                  type="text"
                  id="otp"
                  value={otp}
                  onChange={(e) => setOTP(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-center text-2xl tracking-widest font-mono"
                  disabled={isLoading}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Verifying...' : 'Verify & Login'}
              </button>
            </form>
          )}

          {/* Footer */}
          <div className="mt-6 text-center text-sm text-gray-600">
            <p>Vendor access only</p>
          </div>
        </div>
      </div>
    </div>
  );
}
